import { FC, useState } from 'react';
import ImageIconSolid from '@/components/icons/ImageIconSolid';
import EImageWithFallback from '@/components/everridge/EImageWithFallback';
import {
	GetNewAndOfferListItemResponseDto,
	NewsAndOffersStatus,
} from '@/features/news-and-offers/types/news-and-offers-dto';
import { DateTimeHelper } from '@/utils/dateTimeHelper';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import Archive_02_Icon from '@/components/icons/Archive_02_Icon';
import ComposeIcon from '@/components/icons/ComposeIcon';
import { newsAndOffersConfig } from '@/features/news-and-offers/utils/newsAndOffersConfig';
import MoreVerticalIcon from '@/components/icons/MoreVerticalIcon';
import EDropDownMenu from '@/components/everridge/EDropDownMenu';
import Delete_02_Icon from '@/components/icons/Delete_02_Icon';
import NewAndOfferArchiveModal from '@/features/news-and-offers/components/NewAndOfferArchiveModal';
import { useNewAndOfferDetailsApi } from '@/features/news-and-offers/hooks/useNewAndOfferDetailsApi';
import { ImagesHelper } from '@/utils/ImagesHelper';
import { Roles } from '@/@types/auth.types.ts';
import { useUserRole } from '@/features/identity/hooks/useUserRole.ts';

type Props = {
	showDetails: (id: string) => void;
	onEdit?: (id: string) => void;
} & GetNewAndOfferListItemResponseDto;

const NewsAndOffersCard: FC<Props> = ({
	id,
	imageUrl,
	type,
	title,
	description,
	startDate,
	status,
	showDetails,
	onEdit,
}) => {
	const [showArchiveWarning, setShowArchiveWarning] = useState(false);
	const { t } = useTranslation();
	const userRole = useUserRole();

	const { deleteNewAndOffer, changeNewAndOfferStatus } =
		useNewAndOfferDetailsApi();

	const {
		label,
		icon: Icon,
		className,
	} = newsAndOffersConfig.getCardIcon(type);

	const handleShowArchiveWarning = () => {
		setShowArchiveWarning(true);
	};

	const handleArchiveSelected = async () => {
		await changeNewAndOfferStatus({
			id,
			status: NewsAndOffersStatus.Archived,
		});
		setShowArchiveWarning(false);
	};

	const handleDeleteSelected = async () => {
		await deleteNewAndOffer(id);
	};

	const handlePostSelected = async () => {
		await changeNewAndOfferStatus({
			id,
			status: NewsAndOffersStatus.Active,
		});
		setShowArchiveWarning(false);
	};

	const draftButtons = (
		<>
			<EDropDownMenu
				label={t('elements.buttons.more')}
				icon={MoreVerticalIcon}
				buttonClassName='relative flex-1'>
				<Button
					variant='ghost'
					className='justify-start rounded-none!'
					onClick={() => onEdit?.(id)}>
					<ComposeIcon />
					<span className='truncate text-inherit'>
						{t('elements.buttons.edit')}
					</span>
				</Button>
				<Button
					variant='ghost'
					className='justify-start rounded-none! text-error-1000 active:bg-error-100'
					onClick={handleDeleteSelected}>
					<Delete_02_Icon className='stroke-error-1000' />
					<span className='truncate text-inherit'>
						{t('elements.buttons.delete')}
					</span>
				</Button>
			</EDropDownMenu>
			<Button
				variant='default'
				className='relative flex-1 max-w-1/2'
				onClick={handlePostSelected}>
				<span className='truncate text-inherit'>
					{t('elements.buttons.postDraft')}
				</span>
			</Button>
		</>
	);

	const activeButtons = (
		<>
			<Button
				variant='outline'
				className='relative! flex-1 text-error-1000 active:bg-error-100 max-w-1/2'
				onClick={handleShowArchiveWarning}>
				<Archive_02_Icon className='stroke-error-1000' />
				<span className='truncate text-inherit'>
					{t('elements.buttons.archive')}
				</span>
			</Button>
			<Button
				variant='outline'
				className='relative flex-1 max-w-1/2'
				onClick={() => onEdit?.(id)}>
				<ComposeIcon />
				<span className='truncate text-inherit'>
					{t('elements.buttons.edit')}
				</span>
			</Button>
		</>
	);

	const archivedButtons = (
		<>
			<div className='relative w-full rounded-full'>
				<Button
					variant='outline'
					className='w-full text-error-1000 active:bg-error-100'
					disabled={true}>
					<Archive_02_Icon className='stroke-popover-400' />
					<span className='truncate text-inherit'>
						{t('elements.buttons.archived')}
					</span>
				</Button>
			</div>
		</>
	);

	return (
		<>
			<div className='relative flex flex-col flex-1 border border-popover-100 rounded-2.5xl bg-popover-10 overflow-hidden hover:shadow-custom cursor-pointer'>
				<div
					className='absolute inset-0'
					onClick={() => showDetails(id)}
				/>
				<div className='h-50'>
					<EImageWithFallback
						src={ImagesHelper.getImageUrl(imageUrl)}
						fallbackSVG={ImageIconSolid}
					/>
				</div>
				<div className='p-6 flex flex-col gap-4 select-none'>
					<div className='flex items-center gap-1.5'>
						<Icon className={className} />
						<h5 className='text-sm text-popover-800'>{t(label)}</h5>
					</div>
					<div className='flex flex-col gap-2'>
						<h3 className='line-clamp-1 text-lg font-medium'>
							{title || ''}
						</h3>
						<p className='h-15 line-clamp-3 text-sm text-popover-800'>
							{description || ''}
						</p>
					</div>
					<span className='text-sm text-popover-800'>
						{DateTimeHelper.formatDate(startDate, 2)}
					</span>
				</div>
				{userRole === Roles.admin && (
					<div className='p-6 flex gap-4 border-t border-popover-100 select-none'>
						{status === NewsAndOffersStatus.Draft && draftButtons}
						{status === NewsAndOffersStatus.Active && activeButtons}
						{status === NewsAndOffersStatus.Archived &&
							archivedButtons}
					</div>
				)}
			</div>
			{showArchiveWarning && (
				<NewAndOfferArchiveModal
					onSubmit={handleArchiveSelected}
					onClose={() => setShowArchiveWarning(false)}
				/>
			)}
		</>
	);
};

export default NewsAndOffersCard;
